import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, Search, X, User, Phone } from 'lucide-react';
import { useSearchContacts } from '../../../hooks/useContacts';
import { useDebounce } from '../../../hooks/useDebounce';
import type { Contact } from '../../../types/lead';
import type { ContactPhone } from './types';

export interface ToFieldProps {
  /** Current phone number value (E.164) */
  value: string;
  /** Called when phone number changes */
  onChange: (phoneNumber: string) => void;
  /** Pre-populated phone numbers to show at top of dropdown */
  contactPhones?: readonly ContactPhone[];
  /** Allow searching for additional contacts beyond contactPhones */
  allowContactSearch?: boolean;
  /** Allow typing an arbitrary phone number (not just selecting from list) */
  allowManualEntry?: boolean;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Compact styling */
  compact?: boolean;
}

/** Normalize a phone string to digits-only for comparison. */
function normalizePhone(phone: string): string {
  return phone.replace(/\D/g, '');
}

/** Find the ContactPhone entry matching a phone value. */
function findContactPhone(
  phones: readonly ContactPhone[],
  phone: string
): ContactPhone | undefined {
  const normalized = normalizePhone(phone);
  return phones.find((cp) => normalizePhone(cp.phone) === normalized);
}

/**
 * ToField - Phone number selector with pre-populated contacts and optional search.
 *
 * When `contactPhones` is provided, they appear at the top of the dropdown
 * separated from search results. Selecting a different phone switches the
 * SMS thread (parent component handles thread key based on normalized phone).
 *
 * When `allowContactSearch` is true, a search input is shown in the dropdown
 * to find contacts beyond the pre-populated list.
 */
export function ToField({
  value,
  onChange,
  contactPhones = [],
  allowContactSearch = false,
  allowManualEntry = false,
  disabled = false,
  compact = false,
}: ToFieldProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [manualPhone, setManualPhone] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const debouncedSearch = useDebounce(searchQuery, 300);
  const shouldSearch = allowContactSearch && debouncedSearch.length >= 2;
  const { data: searchResults = [], isLoading: isSearching } =
    useSearchContacts(shouldSearch ? debouncedSearch : '', 8);

  // Filter out contacts that already appear in contactPhones
  const filteredSearchResults = useMemo(() => {
    const prePopulatedNormalized = new Set(
      contactPhones.map((cp) => normalizePhone(cp.phone))
    );
    return searchResults.filter((contact) => {
      const phone = contact.phone || contact.mobilePhone || '';
      return phone && !prePopulatedNormalized.has(normalizePhone(phone));
    });
  }, [searchResults, contactPhones]);

  // Resolve the display label for the current value
  const selectedDisplay = useMemo(() => {
    if (!value) return '';
    const match = findContactPhone(contactPhones, value);
    return match?.label ?? value;
  }, [value, contactPhones]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSearchQuery('');
        setManualPhone('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showDropdown && allowContactSearch) {
      // Delay to allow DOM to render
      requestAnimationFrame(() => {
        searchInputRef.current?.focus();
      });
    }
  }, [showDropdown, allowContactSearch]);

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    setShowDropdown((prev) => !prev);
    setSearchQuery('');
    setManualPhone('');
  }, [disabled]);

  const handleSelectContactPhone = useCallback(
    (cp: ContactPhone) => {
      onChange(cp.phone);
      setShowDropdown(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleSelectSearchResult = useCallback(
    (contact: Contact) => {
      const phone = contact.phone || contact.mobilePhone || '';
      onChange(phone);
      setShowDropdown(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const manualPhoneDigits = manualPhone.replace(/\D/g, '');
  const isManualPhoneValid = manualPhoneDigits.length >= 10 && manualPhoneDigits.length <= 15;

  const handleManualSubmit = useCallback(() => {
    const digits = manualPhone.replace(/\D/g, '');
    if (digits.length >= 10 && digits.length <= 15) {
      const formatted = digits.length === 10 ? `+1${digits}` : `+${digits}`;
      onChange(formatted);
      setManualPhone('');
      setShowDropdown(false);
    }
  }, [manualPhone, onChange]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange('');
      setSearchQuery('');
    },
    [onChange]
  );

  const hasContactPhones = contactPhones.length > 0;
  const hasSearchResults = filteredSearchResults.length > 0;

  return (
    <div ref={containerRef} className="relative flex items-center gap-2">
      <label
        className={`flex items-center gap-1 text-slate-600 whitespace-nowrap ${
          compact ? 'text-xs' : 'text-sm'
        }`}
      >
        <Phone className={compact ? 'w-3 h-3' : 'w-4 h-4'} />
        To:
      </label>

      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggleDropdown}
        disabled={disabled}
        className={`
          relative flex-1 flex items-center justify-between
          border border-slate-300 rounded-lg bg-white text-left
          transition-colors duration-200
          focus:border-navy-500 focus:ring-2 focus:ring-navy-200
          disabled:bg-slate-100 disabled:cursor-not-allowed
          min-h-[44px]
          ${compact ? 'px-2 py-1.5 text-xs' : 'px-3 py-2 text-sm'}
        `}
      >
        <span
          className={`truncate ${
            selectedDisplay ? 'text-slate-900' : 'text-slate-400'
          }`}
        >
          {selectedDisplay || 'Select a contact...'}
        </span>
        <span className="flex items-center gap-1 ml-2 flex-shrink-0">
          {value && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClear(e as unknown as React.MouseEvent);
                }
              }}
              className="text-slate-400 hover:text-slate-600 p-0.5"
              aria-label="Clear selection"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${
              showDropdown ? 'rotate-180' : ''
            }`}
          />
        </span>
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="
            absolute top-full left-0 right-0 z-50 mt-1
            bg-white border border-slate-200 rounded-lg shadow-lg
            max-h-72 overflow-hidden flex flex-col
          "
        >
          {/* Search input (when allowContactSearch) */}
          {allowContactSearch && (
            <div className="p-2 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search contacts..."
                  autoComplete="off"
                  className={`
                    w-full pl-8 pr-3 py-2 text-sm
                    border border-slate-200 rounded-md bg-slate-50
                    focus:border-navy-500 focus:ring-1 focus:ring-navy-200
                    focus:bg-white transition-colors
                    min-h-[44px]
                  `}
                />
              </div>
            </div>
          )}

          {/* Manual phone entry */}
          {allowManualEntry && (
            <div className="p-2 border-b border-slate-100">
              <div className="relative flex gap-2">
                <Phone className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <input
                  type="tel"
                  value={manualPhone}
                  onChange={(e) => setManualPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isManualPhoneValid) {
                      e.preventDefault();
                      handleManualSubmit();
                    }
                  }}
                  placeholder="Type a phone number..."
                  autoComplete="off"
                  className={`
                    flex-1 pl-8 pr-3 py-2 text-sm
                    border border-slate-200 rounded-md bg-slate-50
                    focus:border-navy-500 focus:ring-1 focus:ring-navy-200
                    focus:bg-white transition-colors
                    min-h-[44px]
                  `}
                />
                <button
                  type="button"
                  onClick={handleManualSubmit}
                  disabled={!isManualPhoneValid}
                  className="px-3 py-2 text-sm font-medium text-white bg-sky-600 rounded-md hover:bg-sky-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors min-h-[44px] whitespace-nowrap"
                >
                  Use
                </button>
              </div>
            </div>
          )}

          <div className="overflow-y-auto flex-1">
            {/* Pre-populated contacts section */}
            {hasContactPhones && (
              <>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50">
                  Contacts
                </div>
                {contactPhones.map((cp) => {
                  const isSelected = normalizePhone(cp.phone) === normalizePhone(value);
                  return (
                    <button
                      key={cp.phone}
                      type="button"
                      onClick={() => handleSelectContactPhone(cp)}
                      className={`
                        w-full text-left px-4 py-3
                        transition-colors
                        border-b border-slate-100 last:border-b-0
                        min-h-[44px] flex items-center gap-3
                        ${isSelected ? 'bg-navy-50' : 'hover:bg-slate-50'}
                      `}
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-slate-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {cp.label}
                        </div>
                        <div className="text-xs text-slate-500 truncate">
                          {cp.phone}
                          {cp.contactName && cp.label !== cp.contactName && (
                            <> &middot; {cp.contactName}</>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <span className="text-navy-600 text-xs font-medium">
                          Selected
                        </span>
                      )}
                    </button>
                  );
                })}
              </>
            )}

            {/* Search results section */}
            {allowContactSearch && debouncedSearch.length >= 2 && (
              <>
                {(hasContactPhones || hasSearchResults) && (
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-50">
                    Search Results
                  </div>
                )}
                {isSearching && (
                  <div className="px-4 py-3 text-sm text-slate-500">
                    Searching contacts...
                  </div>
                )}
                {!isSearching && !hasSearchResults && (
                  <div className="px-4 py-3 text-sm text-slate-500">
                    No additional contacts found
                  </div>
                )}
                {filteredSearchResults.map((contact) => (
                  <button
                    key={contact.contactId}
                    type="button"
                    onClick={() => handleSelectSearchResult(contact)}
                    className="
                      w-full text-left px-4 py-3
                      hover:bg-slate-50 transition-colors
                      border-b border-slate-100 last:border-b-0
                      min-h-[44px] flex items-center gap-3
                    "
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                      <User className="w-4 h-4 text-slate-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">
                        {contact.fullName}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {contact.phone || contact.mobilePhone || 'No phone'}
                        {contact.companyName && ` - ${contact.companyName}`}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}

            {/* Empty state when no contactPhones and no search */}
            {!hasContactPhones && !allowContactSearch && (
              <div className="px-4 py-3 text-sm text-slate-500">
                No contacts available
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
